import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Users, TrendingUp, Clock, AlertTriangle, DollarSign, Award, Activity, Target } from 'lucide-react';
import QuadrantView from '../../components/mentor/QuadrantView';
import AttentionSection from '../../components/mentor/AttentionSection';
import ActivityGraphs from '../../components/mentor/ActivityGraphs';
import MentorLayout from '../../components/mentor/MentorLayout';
import axios from 'axios';


import { BACKEND_URL } from '../../config/api';
const MentorDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDate, setShowCustomDate] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
  }, [timeFilter]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('lms_token');
      let url = `${BACKEND_URL}/api/mentor/dashboard/stats?time_filter=${timeFilter}`;
      
      if (timeFilter === 'custom' && customDateRange.start && customDateRange.end) {
        url += `&start_date=${customDateRange.start}&end_date=${customDateRange.end}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshAttention = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      await axios.post(`${BACKEND_URL}/api/mentor/attention-flags/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboardStats();
    } catch (error) {
      console.error('Error refreshing attention flags:', error);
    }
  };

  const dashboardCards = [
    {
      title: 'Total Students Assigned',
      value: stats?.total_students || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Students Activated',
      subtitle: '(within 72 hours)',
      value: stats?.students_activated || 0,
      icon: Target,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Weekly Active Students',
      value: stats?.weekly_active_students || 0,
      icon: Activity,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Avg Weekly Learning Minutes',
      value: Math.round(stats?.avg_weekly_learning_minutes || 0),
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Students Inactive',
      subtitle: '(>7 days)',
      value: stats?.students_inactive_7days || 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      title: 'Students Needing Attention',
      value: stats?.students_needing_attention || 0,
      icon: AlertTriangle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      title: 'Pending Fees Recovery',
      value: `₹${(stats?.pending_fees_recovery || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: 'Avg Quiz Mastery Improvement',
      value: `${Math.round(stats?.avg_quiz_mastery_improvement || 0)}%`,
      icon: TrendingUp,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    },
    {
      title: 'Activation Rate',
      value: `${(stats?.activation_rate || 0).toFixed(1)}%`,
      icon: Award,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    }
  ];

  if (loading && !stats) {
    return (
      <MentorLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </MentorLayout>
    );
  }

  return (
    <MentorLayout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header with Filters */}
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Monitor and support your students</p>
          </div>
          
          {/* Time Filter */}
          <div className="flex flex-wrap gap-2">
            {['daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'all'].map((filter) => (
              <button
                key={filter}
                onClick={() => {
                  setTimeFilter(filter);
                  setShowCustomDate(false);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  timeFilter === filter
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
            <button
              onClick={() => setShowCustomDate(!showCustomDate)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timeFilter === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              Custom Range
            </button>
          </div>
        </div>

        {/* Custom Date Range Picker */}
        {showCustomDate && (
          <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <button
                onClick={() => {
                  setTimeFilter('custom');
                  fetchDashboardStats();
                }}
                disabled={!customDateRange.start || !customDateRange.end}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {dashboardCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">{card.title}</p>
                      {card.subtitle && (
                        <p className="text-xs text-gray-500 mt-0.5">{card.subtitle}</p>
                      )}
                      <p className="text-3xl font-bold text-gray-900 mt-2">{card.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${card.bgColor}`}>
                      <Icon className={`w-6 h-6 ${card.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Activity Graphs */}
        <ActivityGraphs timeFilter={timeFilter} />

        {/* Quadrant View */}
        <QuadrantView />

        {/* Attention Section */}
        <AttentionSection onRefresh={handleRefreshAttention} />
      </div>
    </MentorLayout>
  );
};

export default MentorDashboard;
